import { z } from 'zod';
import { isoDateString } from '../_shared/dates.js';
/**
 * One passenger line on a B2B invoice. `amount` is the flight price billed to
 * the B2B client, in cents (the agent enters it manually, euros -> cents).
 */
export const b2bInvoicePassengerSchema = z.object({
    name: z.string().min(1),
    amount: z.number().int().min(0),
});
/**
 * One PNR section on a B2B invoice. `compagnie` / `libelle` are pre-built on the
 * client from the Affidium booking fetched at gather time, so the server stays a
 * pure renderer (no Affidium round-trip at generation). `insurancePrice` (cents)
 * is present only when insurance was toggled on for this PNR.
 */
export const b2bInvoicePnrSectionSchema = z.object({
    compagnie: z.string().default(''),
    insurancePrice: z.number().int().min(0).nullish(),
    libelle: z.string().default(''),
    passengers: z.array(b2bInvoicePassengerSchema).min(1),
    pnr: z.string().min(1),
});
/**
 * Une prestation annexe (extra) reportée sur la facture B2B. Les champs sont
 * pré-construits côté serveur depuis le `BookingExtra` (comme `compagnie` /
 * `libelle` des PNR) : `libelle` concatène nom + description, `compagnie` porte
 * le fournisseur et `piece` sa référence externe. `amount` est en centimes.
 */
export const b2bInvoiceExtraSchema = z.object({
    amount: z.number().int().min(0),
    compagnie: z.string().default(''),
    libelle: z.string().min(1),
    piece: z.string().default(''),
});
/**
 * Une entrée libre saisie manuellement par l'agent (hors PNR). Rendue comme une
 * ligne de plus dans le tableau des prestations (à l'image d'un vol ou d'un
 * extra), avec une compagnie fixée à `-` au rendu (pas de compagnie saisie).
 * `libelle` et `amount` (centimes) sont requis ; `piece` (n° de pièce) et
 * `voyageur` (passager) sont facultatifs.
 */
export const b2bInvoiceFreeEntrySchema = z.object({
    amount: z.number().int().min(0),
    libelle: z.string().min(1),
    piece: z.string().default(''),
    voyageur: z.string().default(''),
});
/**
 * Un règlement déjà encaissé, reporté sur la facture B2B (section « RÈGLEMENTS »,
 * comme la facture client normale). `amount` est en centimes, `date` est la date
 * d'encaissement (ISO date-only, dérivée du `paidAt`), `method` porte la valeur
 * brute de l'enum `PaymentMethod` (mappée en libellé FR au rendu) et `piece` la
 * référence du paiement (externalId sinon id). Alimenté depuis `booking.payments`
 * (statut PAID) — vide pour les factures manuelles.
 */
export const b2bInvoicePaymentSchema = z.object({
    amount: z.number().int().min(0),
    date: isoDateString(),
    method: z.string().default(''),
    piece: z.string().default(''),
});
/**
 * Génération d'une facture B2B. La facture est toujours rattachée à un client
 * (`clientId` requis) : le serveur en dérive la raison sociale, le contact et
 * l'adresse pour le rendu PDF (source unique de vérité). La facture est ensuite
 * persistée (upload S3 + ligne en base).
 */
export const generateB2bInvoiceSchema = z
    .object({
    /** Dossier source, quand la facture est déclenchée depuis un booking. Absent
     *  pour les factures manuelles multi-PNR créées via /b2b-invoices/new. */
    bookingId: z.string().min(1).optional(),
    clientId: z.string().min(1, 'Le client est requis.'),
    /** Prestations annexes du dossier (extras), reportées après les lignes PNR.
     *  Vide pour les factures manuelles multi-PNR. */
    extras: z.array(b2bInvoiceExtraSchema).default([]),
    /** Entrées libres saisies à la main (hors PNR), cumulables avec les PNR.
     *  Rendues comme des lignes de plus dans le tableau des prestations. */
    freeEntries: z.array(b2bInvoiceFreeEntrySchema).default([]),
    /** Optional manual override; auto-generated when absent. */
    invoiceNumber: z.string().optional(),
    /** Date limite de paiement (ISO), affichée sur le PDF. */
    paymentDeadline: isoDateString(),
    /** Règlements déjà encaissés (statut PAID du dossier), reportés dans la
     *  section « RÈGLEMENTS » et décomptés du total. Vide pour les factures
     *  manuelles (aucun paiement rattaché). */
    payments: z.array(b2bInvoicePaymentSchema).default([]),
    /** Sections PNR (Affidium). Peut être vide si des entrées libres sont
     *  fournies : une facture peut être 100 % entrées libres. */
    pnrSections: z.array(b2bInvoicePnrSectionSchema).default([]),
})
    .superRefine((value, ctx) => {
    if (value.pnrSections.length === 0 && value.freeEntries.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Renseignez au moins un PNR ou une entrée libre.',
            path: ['pnrSections'],
        });
    }
});
